import React, { useState, useEffect, useCallback, useMemo } from "react";
import axios from "axios";
import { connect } from "react-redux";
import moment from "moment";
import { PageHeader, Select, Spin, Empty, Alert, message } from "antd";
import { ReloadOutlined, FileExcelOutlined } from "@ant-design/icons";
import {
  ButtonItem,
  SelectItem,
  RangePickerItem,
  SwitchItem,
} from "../../../../Components/Items";
import EstadosLeyenda from "../../../../Components/EstadosLeyenda";
import { COLORES_ESTADO } from "../MapaHotel/Components/HabitacionCanvas";
import descargarExcelOcupacionFuturo from "./excelOcupacionFuturo";

const { Option } = Select;

// El mismo tope que aplica el backend: un año bisiesto completo. Se valida aquí también para no
// mandar una consulta que ya se sabe que va a regresar el mensaje de error.
const MAX_DIAS = 366;

// Hasta aquí el backend manda la matriz día por día; arriba de esto la agrega por mes. El número vive
// en las dos capas porque la pantalla necesita anticiparlo para avisar qué vista va a salir.
const MAX_DIAS_MATRIZ_DIARIA = 92;

// Los mismos colores que pinta el mapa (ver HabitacionCanvas.js). Se importan en vez de repetirse para
// que un cuarto morado signifique lo mismo en las dos pantallas.
const ESTADOS_LEYENDA = [
  { key: "libre", color: COLORES_ESTADO.libre.borde, label: COLORES_ESTADO.libre.etiqueta },
  { key: "ocupada", color: COLORES_ESTADO.ocupada.borde, label: COLORES_ESTADO.ocupada.etiqueta },
  { key: "programada", color: COLORES_ESTADO.programada.borde, label: "Venta programada" },
  { key: "sinTarifa", color: COLORES_ESTADO.sinTarifa.borde, label: COLORES_ESTADO.sinTarifa.etiqueta },
];

// La columna de habitaciones se queda pegada al hacer scroll horizontal: con 90 columnas, sin esto no
// se sabe de qué cuarto es el renglón que se está viendo.
const ESTILO_COLUMNA_FIJA = {
  position: "sticky",
  left: 0,
  background: "#fff",
  zIndex: 1,
  textAlign: "left",
  minWidth: 190,
};

// Alto de la primera fila de encabezados. Las dos filas se quedan pegadas arriba al hacer scroll: con
// un hotel de cien cuartos, perder la fecha y el porcentaje al bajar deja la matriz ilegible.
const ALTO_ENCABEZADO = 38;

const ESTILO_FILA_PEGADA = { position: "sticky", top: 0, background: "#fff", zIndex: 2 };

// La esquina tiene que ganarle a las dos: se queda pegada hacia arriba y hacia la izquierda a la vez.
const ESTILO_ESQUINA = { ...ESTILO_COLUMNA_FIJA, ...ESTILO_FILA_PEGADA, zIndex: 4 };

const coloresDeCelda = (celda, habitacion) => {
  if (celda && celda.tipo === "ocupada") return COLORES_ESTADO.ocupada;
  if (celda && celda.tipo === "programada") return COLORES_ESTADO.programada;
  if (!habitacion.insumoId) return COLORES_ESTADO.sinTarifa;
  return COLORES_ESTADO.libre;
};

// En la vista mensual los cinco estados no aplican: un mes casi siempre mezcla noches vendidas y
// programadas. La celda se tiñe por qué tan llena está, y el morado solo aparece cuando TODO lo del
// mes es proyección —ahí sí es información: ese cuarto no tiene nada firme todavía—.
const coloresDeMes = (celda, habitacion) => {
  if (!celda || celda.noches === 0) {
    return habitacion.insumoId ? COLORES_ESTADO.libre : COLORES_ESTADO.sinTarifa;
  }
  if (celda.nochesVendidas === 0) return COLORES_ESTADO.programada;
  const proporcion = celda.diasDisponibles > 0 ? celda.noches / celda.diasDisponibles : 0;
  if (proporcion >= 0.9) return COLORES_ESTADO.ocupada;
  if (proporcion >= 0.5) return COLORES_ESTADO.saleHoy;
  return COLORES_ESTADO.libre;
};

// El color del porcentaje del día: verde mientras haya lugar, ámbar cuando aprieta, rojo cuando ya no
// queda nada. Es la lectura que se busca al abrir el reporte, así que tiene que saltar a la vista.
const colorPorcentaje = (porcentaje) => {
  if (porcentaje >= 90) return COLORES_ESTADO.ocupada.texto;
  if (porcentaje >= 60) return COLORES_ESTADO.saleHoy.texto;
  return COLORES_ESTADO.libre.texto;
};

// El detalle de una celda. Se memoriza por estancia y no se recalcula por celda: una estancia de diez
// noches ocupa diez celdas con el mismo texto, y formatear dos fechas con moment en cada una son
// decenas de miles de objetos por render en un hotel grande — que es lo que congela la pestaña mucho
// antes que el número de nodos.
const construirTooltips = (habitaciones) => {
  const cache = new Map();
  const textoDe = (celda) => {
    if (!celda) return "";
    const llave = (celda.ventaId || celda.ventaProgramadaId || "") + "|" + celda.fechaLlegada;
    if (cache.has(llave)) return cache.get(llave);
    const texto = [
      celda.tipo === "programada" ? "Venta programada" : "Venta",
      celda.clienteNombre || "Cliente",
      celda.folio || null,
      `${moment(celda.fechaLlegada).format("DD/MM/YYYY")} → ${moment(celda.fechaSalida).format("DD/MM/YYYY")}`,
    ]
      .filter(Boolean)
      .join(" · ");
    cache.set(llave, texto);
    return texto;
  };
  (habitaciones || []).forEach((habitacion) => (habitacion.celdas || []).forEach(textoDe));
  return textoDe;
};

const NOMBRE_MES = (clave) => {
  const m = moment(clave + "-01", "YYYY-MM-DD");
  return m.isValid() ? m.format("MMM YYYY") : clave;
};

//Reporte "Ocupacion a Futuro": la matriz habitación × día de un rango, con la ocupación ya vendida y
//la que todavía vive en una venta programada.
//
//Es el complemento del Mapa de Hotel: el mapa contesta "cómo está el hotel hoy" —una fecha, el plano
//completo— y esto contesta "cómo viene la temporada" —muchas fechas, una tabla—. Comparte con el mapa
//los colores y el criterio de qué cuenta como ocupado, para que no puedan contar cosas distintas.
const OcupacionFuturo = ({ empresaId, headersToken }) => {
  const [todosProyectos, setTodosProyectos] = useState([]);
  const [proyectoId, setProyectoId] = useState(undefined);
  const [niveles, setNiveles] = useState([]);
  const [nivelHotelId, setNivelHotelId] = useState(undefined);
  const [rango, setRango] = useState([moment(), moment().add(29, "days")]);
  const [incluirProgramadas, setIncluirProgramadas] = useState(true);
  const [cargando, setCargando] = useState(false);
  const [datos, setDatos] = useState(null);
  const [error, setError] = useState(null);

  const desdeTexto = rango && rango[0] ? rango[0].format("YYYY-MM-DD") : undefined;
  const hastaTexto = rango && rango[1] ? rango[1].format("YYYY-MM-DD") : undefined;

  useEffect(() => {
    axios
      .post("proyectos/selectProyectos", { empresaId }, { headers: headersToken })
      .then((res) => {
        if (res.data.status === 404) return;
        setTodosProyectos(res.data || []);
      })
      .catch((error) => console.error("Error al cargar los hoteles:", error));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Al cambiar de hotel se recargan sus pisos. A diferencia del mapa aquí NO se autoselecciona el
  // primero: el valor útil por default es "todos los pisos", que es la ocupación del hotel completo.
  useEffect(() => {
    if (!proyectoId) {
      setNiveles([]);
      setNivelHotelId(undefined);
      return;
    }
    setNivelHotelId(undefined);
    axios
      .post("hoteleria/selectNivelesHotel", { empresaId, proyectoId }, { headers: headersToken })
      .then((res) => {
        if (res.data.success !== 2) return;
        setNiveles(res.data.niveles || []);
      })
      .catch((error) => console.error("Error al cargar los pisos:", error));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [empresaId, headersToken, proyectoId]);

  const consultar = useCallback(() => {
    if (!proyectoId || !desdeTexto || !hastaTexto) {
      setDatos(null);
      return;
    }
    setCargando(true);
    axios
      .post(
        "hoteleria/reporteOcupacionFuturo",
        { empresaId, proyectoId, nivelHotelId, desde: desdeTexto, hasta: hastaTexto, incluirProgramadas },
        { headers: headersToken }
      )
      .then((res) => {
        if (res.data.success === 2) {
          setDatos(res.data);
          setError(null);
          return;
        }
        setDatos(null);
        // El mensaje se deja en pantalla y no solo en un toast que se va a los tres segundos: si no,
        // la tabla anterior se queda ahí y parece que el reporte respondió a lo que se pidió.
        setError(res.data.message || "No se pudo generar el reporte");
      })
      .catch((error) => {
        console.error("Error al generar el reporte de ocupación:", error);
        setDatos(null);
        setError("No se pudo consultar el reporte. Revisa tu conexión e intenta de nuevo.");
      })
      .finally(() => setCargando(false));
  }, [empresaId, headersToken, proyectoId, nivelHotelId, desdeTexto, hastaTexto, incluirProgramadas]);

  useEffect(() => {
    consultar();
  }, [consultar]);

  const cambiarRango = (valores) => {
    if (!valores || !valores[0] || !valores[1]) return;
    const diasPedidos = valores[1].diff(valores[0], "days") + 1;
    if (diasPedidos > MAX_DIAS) {
      // Se recorta en vez de descartar la selección: el picker es controlado, así que ignorar el
      // cambio lo regresaba al rango anterior sin explicación y dejaba en pantalla el resultado viejo
      // — se veía como si el reporte hubiera contestado mal.
      const recortado = [valores[0], valores[0].clone().add(MAX_DIAS - 1, "days")];
      setRango(recortado);
      message.warning(
        `El periodo máximo es de ${MAX_DIAS} días. Se ajustó al ${recortado[1].format("DD/MM/YYYY")}.`
      );
      return;
    }
    setRango(valores);
  };

  const granularidad = datos?.granularidad || "dia";
  const esMensual = granularidad === "mes";
  const dias = datos?.dias || [];
  const periodos = datos?.periodos || [];
  // Memorizado porque `|| []` crea un arreglo nuevo en cada render, y eso hacía que el useMemo de los
  // tooltips —que existe justo para no recalcularlos— se recalculara siempre.
  const habitaciones = useMemo(() => datos?.habitaciones || [], [datos]);
  const resumenPorDia = datos?.resumenPorDia || [];
  const resumenPorPeriodo = datos?.resumenPorPeriodo || [];
  const columnas = esMensual ? periodos : dias;
  const resumen = esMensual ? resumenPorPeriodo : resumenPorDia;
  const tooltipDeCelda = useMemo(() => construirTooltips(habitaciones), [habitaciones]);

  // Cuántos días abarca lo que está elegido AHORA en el picker, que no es lo mismo que lo que trae la
  // respuesta: sirve para avisar qué vista va a salir antes de que llegue.
  const diasElegidos =
    rango && rango[0] && rango[1] ? rango[1].diff(rango[0], "days") + 1 : 0;
  const nombreHotel = todosProyectos.find((valor) => valor._id === proyectoId)?.nombre || "";
  const nombreNivel = niveles.find((valor) => valor._id === nivelHotelId)?.nombre || "Todos los pisos";

  const exportar = () => {
    if (!datos || !habitaciones.length) return;
    descargarExcelOcupacionFuturo({
      datos,
      nombreHotel,
      nombreNivel,
      incluirProgramadas,
    });
  };

  return (
    <>
      <PageHeader
        title="Ocupacion a Futuro"
        style={{ border: "1px solid rgb(235,237,240)", backgroundColor: "white", marginBottom: 10 }}
        extra={
          <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
            <ButtonItem
              blanco
              size="small"
              icon={<ReloadOutlined />}
              disabled={!proyectoId}
              onClick={consultar}
            >
              Actualizar
            </ButtonItem>
            <ButtonItem
              size="small"
              className="botonAgregar"
              icon={<FileExcelOutlined />}
              disabled={!habitaciones.length}
              onClick={exportar}
            >
              Descargar Excel
            </ButtonItem>
          </div>
        }
      />

      <div style={{ background: "#fff", borderRadius: 14, padding: 16 }}>
        <div
          style={{ display: "flex", gap: 12, alignItems: "flex-end", flexWrap: "wrap", marginBottom: 12 }}
        >
          <div>
            <div style={{ fontSize: 12, color: "#6b7280", marginBottom: 2 }}>Hotel</div>
            <SelectItem width={240} placeholder="Hotel" value={proyectoId} onChange={setProyectoId}>
              {todosProyectos.map((valorProyecto) => (
                <Option key={valorProyecto._id} value={valorProyecto._id}>
                  {valorProyecto.nombre}
                </Option>
              ))}
            </SelectItem>
          </div>

          <div>
            <div style={{ fontSize: 12, color: "#6b7280", marginBottom: 2 }}>Piso</div>
            <SelectItem
              width={200}
              placeholder="Todos los pisos"
              value={nivelHotelId}
              disabled={!proyectoId}
              allowClear
              onChange={setNivelHotelId}
            >
              {niveles.map((valorNivel) => (
                <Option key={valorNivel._id} value={valorNivel._id}>
                  {valorNivel.nombre}
                </Option>
              ))}
            </SelectItem>
          </div>

          <div>
            <div style={{ fontSize: 12, color: "#6b7280", marginBottom: 2 }}>
              Periodo (máx. {MAX_DIAS} días)
            </div>
            <RangePickerItem width={260} value={rango} allowClear={false} onChange={cambiarRango} />
            {/* Qué vista va a salir, antes de pedirla: pasando los 3 meses la tabla cambia sola de
                día a mes y sin avisar eso se siente como si el reporte hubiera hecho otra cosa. */}
            <div style={{ fontSize: 11, color: "#6b7280", marginTop: 2 }}>
              {diasElegidos > MAX_DIAS_MATRIZ_DIARIA
                ? `${diasElegidos} días · resumen por mes`
                : `${diasElegidos} días · detalle por día`}
            </div>
          </div>

          <div>
            <div style={{ fontSize: 12, color: "#6b7280", marginBottom: 2 }}>Ventas programadas</div>
            <SwitchItem
              checked={incluirProgramadas}
              onChange={setIncluirProgramadas}
              checkedChildren="Incluir"
              unCheckedChildren="Omitir"
            />
          </div>
        </div>

        {error && (
          <Alert type="warning" showIcon style={{ marginBottom: 12 }} message={error} />
        )}

        <Alert
          type="info"
          showIcon
          style={{ marginBottom: 12 }}
          message="La ocupación no se guarda: se deriva de las ventas y de las ventas programadas"
          description={
            esMensual
              ? "Cada celda son las noches ocupadas del mes: las vendidas más las que compromete una venta programada. Pasa el mouse encima para ver el desglose."
              : "Una celda morada es una reserva que todavía no existe como venta: la va a generar una venta programada al llegar su fecha. La celda roja ya es una venta capturada."
          }
        />

        {datos && datos.sinTarifa > 0 && (
          <Alert
            type="warning"
            showIcon
            style={{ marginBottom: 12 }}
            message={`${datos.sinTarifa} de ${datos.total} habitación(es) no tienen insumo de tarifa`}
            description="No se pueden vender, pero sí cuentan en el total, así que bajan el porcentaje de ocupación. Asígnales su insumo en la configuración del mapa."
          />
        )}

        {!esMensual && (
          <div style={{ marginBottom: 8 }}>
            <EstadosLeyenda estados={ESTADOS_LEYENDA} compact />
          </div>
        )}

        <Spin spinning={cargando}>
          {!proyectoId ? (
            <Empty description="Elige un hotel para ver su ocupación" style={{ padding: 60 }} />
          ) : error ? (
            // Sin esto, cualquier error respondía con "no tiene habitaciones configuradas", que manda
            // a revisar el catálogo cuando el problema era otro.
            <Empty description="No se pudo mostrar la ocupación" style={{ padding: 60 }} />
          ) : !habitaciones.length ? (
            <Empty description="Este hotel todavía no tiene habitaciones configuradas" style={{ padding: 60 }} />
          ) : (
            // La tabla es más ancha que la pantalla por diseño (una columna por día), así que el
            // scroll horizontal vive en este contenedor y no en el body de la página. No se usa la
            // clase `.table` del proyecto: trae `table-layout: fixed` y `width: 100%`, que con 90
            // columnas aplastaría todo hasta volverlo ilegible.
            <div style={{ overflowX: "auto", border: "1px solid #eef0f6", borderRadius: 8 }}>
              <table style={{ borderCollapse: "collapse", tableLayout: "auto", width: "auto" }}>
                <thead>
                  <tr>
                    <th className="th-border-up" style={{ ...ESTILO_ESQUINA, minWidth: 190 }}>
                      Habitación
                    </th>
                    {columnas.map((valorColumna, indice) =>
                      esMensual ? (
                        <th
                          key={valorColumna.clave}
                          className="th-border-up"
                          style={{ ...ESTILO_FILA_PEGADA, minWidth: 74, padding: "4px 6px", fontSize: 12, textTransform: "capitalize" }}
                          title={`${valorColumna.diasEnPeriodo} día(s) dentro del periodo`}
                        >
                          {NOMBRE_MES(valorColumna.clave)}
                        </th>
                      ) : (
                        <th
                          key={indice}
                          className="th-border-up"
                          style={{ ...ESTILO_FILA_PEGADA, minWidth: 46, padding: "2px 4px" }}
                        >
                          <div style={{ fontSize: 10, color: "#6b7280", textTransform: "capitalize" }}>
                            {moment(valorColumna).format("ddd")}
                          </div>
                          <div style={{ fontSize: 12 }}>{moment(valorColumna).format("DD/MM")}</div>
                        </th>
                      )
                    )}
                  </tr>
                  <tr>
                    <th className="th-border-up" style={{ ...ESTILO_ESQUINA, top: ALTO_ENCABEZADO }}>
                      % de ocupación
                    </th>
                    {resumen.map((valorResumen, indice) => {
                      const porcentaje = incluirProgramadas
                        ? valorResumen.porcentajeOcupacionConProgramadas
                        : valorResumen.porcentajeOcupacion;
                      const detalle = esMensual
                        ? `${valorResumen.nochesVendidas} noche(s) vendida(s) · ${valorResumen.nochesProgramadas} programada(s) · ${valorResumen.nochesLibres} libre(s) de ${valorResumen.nochesDisponibles}`
                        : `${valorResumen.ocupadas} vendida(s) · ${valorResumen.programadas} programada(s) · ${valorResumen.libres} libre(s)`;
                      return (
                        <th
                          key={indice}
                          className="th-border-up"
                          style={{
                            ...ESTILO_FILA_PEGADA,
                            top: ALTO_ENCABEZADO,
                            padding: "2px 4px",
                            fontSize: 11,
                            color: colorPorcentaje(porcentaje),
                          }}
                          title={detalle}
                        >
                          {porcentaje}%
                        </th>
                      );
                    })}
                  </tr>
                </thead>
                <tbody>
                  {habitaciones.map((valorHabitacion) => (
                    <tr key={valorHabitacion._id}>
                      <td className="td-border" style={{ ...ESTILO_COLUMNA_FIJA, padding: "2px 6px" }}>
                        <div style={{ fontWeight: 700 }}>
                          {valorHabitacion.numero}
                          {valorHabitacion.nombre ? ` - ${valorHabitacion.nombre}` : ""}
                        </div>
                        <div style={{ fontSize: 11, color: "#6b7280" }}>
                          {valorHabitacion.nivelNombre}
                          {valorHabitacion.insumoDescripcion
                            ? ` · ${valorHabitacion.insumoDescripcion}`
                            : " · sin tarifa"}
                        </div>
                      </td>
                      {esMensual
                        ? (valorHabitacion.celdasPeriodo || []).map((valorCelda, indice) => {
                            const colores = coloresDeMes(valorCelda, valorHabitacion);
                            return (
                              <td
                                key={indice}
                                className="td-border-center"
                                title={
                                  valorCelda.noches > 0
                                    ? `${valorCelda.noches} de ${valorCelda.diasDisponibles} noches · ${valorCelda.nochesVendidas} vendidas, ${valorCelda.nochesProgramadas} programadas`
                                    : `Libre las ${valorCelda.diasDisponibles} noches del periodo`
                                }
                                style={{
                                  background: colores.fondo,
                                  color: colores.texto,
                                  fontSize: 11,
                                  fontWeight: 700,
                                  padding: "3px 4px",
                                }}
                              >
                                {valorCelda.noches > 0 ? valorCelda.noches : ""}
                              </td>
                            );
                          })
                        : valorHabitacion.celdas.map((valorCelda, indice) => {
                            const colores = coloresDeCelda(valorCelda, valorHabitacion);
                            return (
                              // El detalle va en el `title` nativo y no en un Tooltip de antd: son
                              // habitaciones × días celdas —fácil pasan de dos mil— y montar un
                              // componente por cada una para algo que solo se ve al pasar el mouse
                              // costaría más que toda la tabla. Igual que HabitacionCanvas.
                              <td
                                key={indice}
                                className="td-border-center"
                                title={tooltipDeCelda(valorCelda)}
                                style={{
                                  background: colores.fondo,
                                  color: colores.texto,
                                  fontSize: 10,
                                  fontWeight: 700,
                                  padding: 2,
                                }}
                              >
                                {valorCelda ? (valorCelda.tipo === "programada" ? "P" : "V") : ""}
                              </td>
                            );
                          })}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Spin>
      </div>
    </>
  );
};

const mapStateToProps = (state) => ({
  headersToken: state.user.headersToken,
  empresaId: state.empresa.id,
});

export default connect(mapStateToProps)(OcupacionFuturo);
